/**
 * Save recorded WebM blobs — directory picker when available, else download.
 */

const IDB_NAME = "coykoi-shot";
const IDB_STORE = "handles";
const IDB_KEY = "outputDir";

export type ShotSaveResult =
  | { mode: "directory"; name: string }
  | { mode: "download"; name: string };

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
  });
}

export async function persistDirectoryHandle(
  handle: FileSystemDirectoryHandle,
): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).put(handle, IDB_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB put failed"));
  });
  db.close();
}

export async function loadDirectoryHandle(): Promise<FileSystemDirectoryHandle | null> {
  try {
    const db = await openDb();
    const handle = await new Promise<FileSystemDirectoryHandle | null>(
      (resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readonly");
        const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
        req.onsuccess = () => {
          resolve((req.result as FileSystemDirectoryHandle | undefined) ?? null);
        };
        req.onerror = () => reject(req.error ?? new Error("IndexedDB get failed"));
      },
    );
    db.close();
    return handle;
  } catch {
    return null;
  }
}

export function supportsDirectoryPicker(): boolean {
  return typeof window !== "undefined" && "showDirectoryPicker" in window;
}

export async function pickOutputDirectory(): Promise<FileSystemDirectoryHandle | null> {
  if (!supportsDirectoryPicker()) return null;
  try {
    const handle = await window.showDirectoryPicker!({
      id: "coykoi-shot-output",
      mode: "readwrite",
      startIn: "videos",
    });
    await persistDirectoryHandle(handle);
    return handle;
  } catch (err) {
    // User cancelled
    if (err instanceof DOMException && err.name === "AbortError") return null;
    throw err;
  }
}

async function ensureWritable(
  handle: FileSystemDirectoryHandle,
): Promise<boolean> {
  const query = handle.queryPermission?.bind(handle);
  const request = handle.requestPermission?.bind(handle);
  if (!query || !request) return true;
  const q = await query({ mode: "readwrite" });
  if (q === "granted") return true;
  const r = await request({ mode: "readwrite" });
  return r === "granted";
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function saveShotBlob(
  blob: Blob,
  filename: string,
  directory: FileSystemDirectoryHandle | null,
): Promise<ShotSaveResult> {
  if (directory) {
    const ok = await ensureWritable(directory);
    if (ok) {
      const file = await directory.getFileHandle(filename, { create: true });
      const writable = await file.createWritable();
      await writable.write(blob);
      await writable.close();
      return { mode: "directory", name: directory.name };
    }
  }
  downloadBlob(blob, filename);
  return { mode: "download", name: filename };
}
